import React from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Reusable confirmation modal component
 * Replaces native window.confirm with a styled modal
 */
const ConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Confirm Action',
    message = 'Are you sure you want to proceed?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'danger', // 'danger' | 'warning' | 'info'
    isLoading = false,
    icon: CustomIcon = null
}) => {
    if (!isOpen) return null;

    const variantStyles = {
        danger: {
            iconBg: 'dark:bg-red-500/20 bg-red-100',
            iconColor: 'dark:text-red-400 text-red-600',
            confirmBg: 'bg-red-600 hover:bg-red-500',
            confirmShadow: 'shadow-red-900/20'
        },
        warning: {
            iconBg: 'dark:bg-yellow-500/20 bg-yellow-100',
            iconColor: 'dark:text-yellow-400 text-yellow-600',
            confirmBg: 'bg-yellow-600 hover:bg-yellow-500',
            confirmShadow: 'shadow-yellow-900/20'
        },
        info: {
            iconBg: 'dark:bg-yellow-500/20 bg-yellow-100',
            iconColor: 'dark:text-yellow-400 text-yellow-600',
            confirmBg: 'bg-yellow-600 hover:bg-yellow-500',
            confirmShadow: 'shadow-yellow-900/20'
        }
    };

    const styles = variantStyles[variant] || variantStyles.danger;
    const Icon = CustomIcon || AlertTriangle;

    return (
        <div
            className="fixed inset-0 z-[130] flex items-center justify-center dark:bg-slate-900/80 bg-slate-500/50 backdrop-blur-sm px-4"
            onClick={onClose}
        >
            <div
                className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl max-w-md w-full shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b dark:border-slate-800 border-slate-200">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl ${styles.iconBg} flex items-center justify-center`}>
                            <Icon className={`w-5 h-5 ${styles.iconColor}`} />
                        </div>
                        <h3 className="text-lg font-bold dark:text-white text-slate-900">{title}</h3>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isLoading}
                        className="text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                        {message}
                    </p>
                </div>

                {/* Footer */}
                <div className="flex gap-3 p-6 border-t dark:border-slate-800 border-slate-200">
                    <button
                        onClick={onClose}
                        disabled={isLoading}
                        className="flex-1 px-4 py-2.5 rounded-xl border dark:border-slate-700 border-slate-200 dark:text-slate-300 text-slate-600 font-medium hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-white transition-colors disabled:opacity-50"
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isLoading}
                        className={`flex-1 px-4 py-2.5 rounded-xl ${styles.confirmBg} text-white font-bold transition-all shadow-lg ${styles.confirmShadow} disabled:opacity-50 flex items-center justify-center gap-2`}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Processing...
                            </>
                        ) : (
                            confirmText
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
